import {
  CloseOutlined,
  DeleteOutlined,
  EditTwoTone,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
} from "antd";
import React from "react";
import CTConfiguration, {
  CapitalAllowanceAssetCategories,
  CapitalAllowanceAssetCategoriesOptionList,
  CTConfigurationCapitalAllowanceBase,
} from "../../Services/CTConfigurationService";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";
import { adminContext } from "../../Common/PageRoute";
import FImage from "../../images/FImage(2).svg";
import { Utils } from "../../Utilities/Utils";
import { useUser } from "../../Context/UserContext";
const { RangePicker } = DatePicker;

interface CTConfigurationCapitalAllowance extends CTConfigurationCapitalAllowanceBase {
  category: CapitalAllowanceAssetCategories;
  rate: number;
  isFYA100?: boolean;
  isEnhancedCA?: boolean;
}

const CapitalAllowance = () => {
  const { openNotification } = React.useContext(adminContext);
  //const navigate = useNavigate();
  const [form] = useForm();
  const [loading, setLoading] = React.useState(false);
  const [isEdit, setIsEdit] = React.useState(false);
  const [CapitalAllowanceList, setCapitalAllowanceList] = React.useState<CTConfigurationCapitalAllowance[]>(
    [],
  );
  const [dateRange, setDateRange] = React.useState<any>(null);

  const { isSubAdmin } = useUser();
  const showEdit = !isSubAdmin();
  const [CAACategory, setCAACategory] = React.useState<{
    type: CapitalAllowanceAssetCategories;
    name: string;
  }>({
    type: CapitalAllowanceAssetCategories.MachineryAndPlantSpecial,
    name: "Machinery and plant (Special Rate Pool)",
  });
  const OnCancelClick = () => {
    setIsEdit(false);
    setFormValues(CapitalAllowanceList);
  };

  const OnSaveClick = async () => {
    let rawValues = form.getFieldValue("CategoryTypeValues");
    let values = rawValues.map((item: any) => ({
      ...item,
      fromDate: item.fromDate
        ? dayjs(item.fromDate).format("YYYY-MM-DD")
        : null,
      toDate: item.toDate ? dayjs(item.toDate).format("YYYY-MM-DD") : null,
    }));
    let res = await CTConfiguration.postCapitalAllowances(
      CAACategory.type,
      values,
    );
    if (res.status) {
      setIsEdit(false);
      setCapitalAllowanceList(res.result);
      setFormValues(res.result);
      openNotification("success", "Details updated successfully");
    } else {
      openNotification("error", res.message);
      setLoading(false);
    }
  };

  const setFormValues = (list: any) => {
    form.setFieldsValue({
      CategoryTypeValues: list.map((item: any) => ({
        name: item.name,
        fromDate: item.fromDate ? dayjs(item.fromDate) : undefined,
        toDate: item.toDate ? dayjs(item.toDate) : undefined,
        rate: item.rate,
        isFYA100: item.isFYA100,
        isEnhancedCA: item.isEnhancedCA,
      })),
    });
  };

  React.useEffect(() => {
    setIsEdit(false);
    setLoading(true);
    CTConfiguration.getCapitalAllowances(CAACategory.type).then((res) => {
      if (res.status) {
        setCapitalAllowanceList(res.result);
        setFormValues(res.result);
        setLoading(false);
      } else {
        openNotification("error", res.message);
        setLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CAACategory.type]);

  React.useEffect(() => {
    if (!dateRange || dateRange.length !== 2) {
      // No filter → show full list
      setFormValues(CapitalAllowanceList);
      return;
    }

    const [start, end] = dateRange;

    const filtered = CapitalAllowanceList.filter((item: any) => {
      if (!item.fromDate || !item.toDate) return false;

      const from = dayjs(item.fromDate);
      const to = dayjs(item.toDate);

      // overlap logic
      return (
        from.isSameOrBefore(end, "day") &&
        to.isSameOrAfter(start, "day")
      );
    });

    setFormValues(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, CapitalAllowanceList]);

  return (
    <Spin spinning={loading} indicator={<LoadingOutlined />}
    >
      <Form form={form}>
        <Form.List name="CategoryTypeValues">
          {(fields, { add, remove }, { errors }) => (
            <Row>
              <Col span={24} className="card pb-3 border-rounded-1">
                <Row
                  gutter={[8, 8]}
                  className="py-2  border-bottom-table"
                  align="middle"
                >
                  {/* ================= ADD ================= */}
                  <Col xs={24} sm={12} md={4} lg={4} xl={3} xxl={2}>
                    {
                      !isSubAdmin() ?
                        <Form.Item className="custom-form-item">
                          <Button
                            block
                            type="primary"
                            onClick={() => {
                              if (isEdit) add();
                              else
                                openNotification(
                                  "warning",
                                  "Please enable edit mode to add items"
                                );
                            }}
                          >
                            <PlusOutlined />
                            Item
                          </Button>
                        </Form.Item>
                        : null
                    }
                  </Col>

                  {/* ================= Date ================= */}
                  <Col xs={24} sm={12} md={{ span: 8, offset: 1 }} lg={{ span: 6, offset: 5 }} xl={{ span: 5, offset: 8 }} xxl={{ span: 4, offset: 12 }}>
                    {/* <RangePicker
                      format="DD/MM/YYYY"
                      allowClear
                      onChange={(dates) => {
                        setDateRange(dates);
                      }}
                      style={{ width: "100%" }}
                    /> */}
                  </Col>

                  {/* ================= Type ================= */}
                  <Col
                    xs={showEdit ? 22 : 24}
                    sm={showEdit ? 22 : 24}
                    md={showEdit ? 10 : 11}
                    lg={showEdit ? 8 : 9}
                    xl={showEdit ? 7 : 8}
                    xxl={showEdit ? 5 : 6}
                  >
                    <Select
                      style={{ width: "100%" }}

                      // className="line-height-28"
                      value={CAACategory.type}
                      options={CapitalAllowanceAssetCategoriesOptionList}
                      onChange={(val: any, item: any) => {
                        setLoading(true);
                        setCAACategory({ type: val, name: item?.label });
                      }}
                    />
                  </Col>

                  {/* ================= Edit ================= */}
                  {showEdit && (
                    <Col xs={2} sm={2} md={1} lg={1} xl={1} xxl={1} style={{ display: "flex", justifyContent: "center" }}>
                      <span
                        className="edit-btn"

                        onClick={() => {
                          setIsEdit(!isEdit);
                          setDateRange(null);
                        }}
                      >
                        <EditTwoTone />
                      </span>
                    </Col>
                  )}
                </Row>

                {fields?.length === 0 ?
                  <Row
                    style={{ height: "500px" }}
                    align="middle"
                    justify="center"
                  >
                    <Image preview={false} src={FImage} alt="No Items" />
                  </Row>
                  :
                  fields.map((field: any, index: any) => (
                    <Col
                      span={24}
                      className="border border-rounded-1 pb-2 mt-3"
                    >
                      <Row
                        className="px-3 py-1 font-bold border-bottom"
                        justify="space-between"
                      >
                        <div>
                          {CAACategory?.name} : Item {index + 1}
                        </div>
                        <div>
                          {CapitalAllowanceList?.length <= index && (
                            <span
                              className="delete-btn"
                              onClick={() => remove(field.name)}
                            >
                              <DeleteOutlined
                                style={{ color: "red", fontSize: 18 }}
                              />
                            </span>
                          )}
                        </div>
                      </Row>
                      <Row className="px-3 pt-1">
                        <Col span={8}>
                          <Form.Item
                            className="custom-form-item"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 14 }}
                            label="Name"
                            name={[field.name, "name"]}
                          >
                            <Input
                              placeholder="Name"
                              onKeyPress={(e) =>
                                Utils.ValidateCharSpaceAndNumberOnly(e)
                              }
                              disabled={isEdit ? false : true}
                              className="line-height-28"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            className="custom-form-item"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 12 }}
                            label="From date"
                            name={[field.name, "fromDate"]}
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              placeholder="From date"
                              format="DD/MM/YYYY"
                              disabled={isEdit ? false : true}
                              className="line-height-28"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            className="custom-form-item"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 12 }}
                            label="To date"
                            name={[field.name, "toDate"]}
                          >
                            <DatePicker
                              style={{ width: "100%" }}
                              placeholder="To date"
                              format="DD/MM/YYYY"
                              disabled={isEdit ? false : true}
                              className="line-height-28"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row className="px-3 pt-1">
                        <Col span={8}>
                          <Form.Item
                            className="custom-form-item"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 14 }}
                            label="Rate"
                            name={[field.name, "rate"]}
                          >
                            <InputNumber
                              controls={false}
                              placeholder="Rate"
                              className="line-height-28"
                              disabled={isEdit ? false : true}
                              onKeyPress={(e) =>
                                Utils.ValidatePercentageOnly(e)
                              }
                              prefix="%"
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          span={8}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Form.Item
                            className="custom-form-item"
                            valuePropName="checked"
                            name={[field.name, "isFYA100"]}
                          >
                            <Checkbox
                              disabled={isEdit ? false : true}
                              className="line-height-28"
                            >
                              First year allowance at 100%
                            </Checkbox>
                          </Form.Item>
                        </Col>
                        <Col
                          span={8}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Form.Item
                            className="custom-form-item"
                            valuePropName="checked"
                            name={[field.name, "isEnhancedCA"]}
                          >
                            <Checkbox
                              disabled={isEdit ? false : true}
                              className="line-height-28"
                            >
                              100% Allowance (Enhanced CAs)
                            </Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  ))
                }
                {isEdit && (
                  <Row className="px-3 pt-3">
                    <Button
                      loading={loading}
                      type="primary"
                      onClick={OnSaveClick}
                    >
                      <SaveOutlined /> Save
                    </Button>
                    <Button
                      loading={loading}
                      className="ms-2"
                      onClick={OnCancelClick}
                    >
                      <CloseOutlined /> Cancel
                    </Button>
                  </Row>
                )}
              </Col>
            </Row>
          )}
        </Form.List>
      </Form>
    </Spin>
  );
};

export default CapitalAllowance;